import React, { useEffect, useRef, useState } from 'react';
import { useUserContext, confirmRazorpayPayment } from '../../app/protectedRoute/UserContext';


interface RazorpayCheckoutProps {
  bookingId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

const RazorpayCheckout: React.FC<RazorpayCheckoutProps> = ({
  bookingId,
  firstName,
  lastName,
  email,
  phoneNumber,
}) => {
  const { state, dispatch } = useUserContext();
  const { razorpayOrderId, razorpayAmount, paymentLoading, paymentError, paymentSuccess } = state;
  const razorpayInstance = useRef<any>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    if ((window as any).Razorpay) {
      setIsScriptLoaded(true);
      return;
    }
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener('load', () => setIsScriptLoaded(true));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => setIsScriptLoaded(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    console.log("RazorpayCheckout: useEffect for instance creation triggered");
    if (isScriptLoaded && razorpayOrderId) {
      console.log('RazorpayCheckout: Razorpay script loaded and razorpayOrderId is available.');
      console.log('RazorpayCheckout: razorpayOrderId:', razorpayOrderId);
      console.log('RazorpayCheckout: razorpayAmount:', razorpayAmount);

      if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
        console.warn("RazorpayCheckout: Razorpay Key ID (REACT_APP_RAZORPAY_KEY_ID) is not set.");
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: razorpayAmount,
        currency: 'INR',
        name: 'Social Bunkr',
        description: 'Booking Payment',
        order_id: razorpayOrderId,
        handler: async function (response: any) {
          console.log("RazorpayCheckout: Payment handler called");
          await confirmRazorpayPayment(dispatch, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            booking_id: bookingId,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone_number: phoneNumber,
          });
          console.log("RazorpayCheckout: confirmRazorpayPayment called");
        },
        prefill: {
          name: `${firstName} ${lastName}`,
          email: email,
          contact: phoneNumber,
        },
        notes: {
          booking_id: bookingId,
        },
      };

      try {
        console.log("RazorpayCheckout: Attempting to create Razorpay instance");
        razorpayInstance.current = new (window as any).Razorpay(options);
        console.log('RazorpayCheckout: Razorpay instance created and opening:', razorpayInstance.current);
        razorpayInstance.current.open();
        console.log("RazorpayCheckout: Razorpay instance open() called");
      } catch (error) {
        console.error("RazorpayCheckout: Error creating Razorpay instance:", error);
      }
    }
  }, [isScriptLoaded, razorpayOrderId, razorpayAmount, bookingId, dispatch, firstName, lastName, email, phoneNumber]);

  useEffect(() => {
    console.log("RazorpayCheckout: useEffect for payment success/error triggered");
    if (paymentSuccess) {
      console.log('RazorpayCheckout: paymentSuccess is true. Razorpay modal should close automatically.');
      if (razorpayInstance.current) {
        console.log('RazorpayCheckout: Razorpay modal close() was not called programmatically.');
      }
    }
    if (paymentError) {
      console.log('RazorpayCheckout: paymentError is true. Razorpay modal should close automatically.');
      if (razorpayInstance.current) {
        console.log('RazorpayCheckout: Razorpay modal close() was not called programmatically.');
      }
    }
  }, [paymentSuccess, paymentError]);

  return null; // This component doesn't render anything directly
};

export default RazorpayCheckout;
